import React from 'react';
import ReactDOM from 'react-dom/client';
import { ArrowRight, CheckCircle2, FileText, ShieldCheck, Workflow } from 'lucide-react';
import { LeadForm } from './components/LeadForm';
import { brand, offer, phases, promises, solutions } from './content/landing';
import './styles.css';

function track(name: string) {
  window.dispatchEvent(new CustomEvent('mfc:event', { detail: { name } }));
}

function App() {
  return (
    <main>
      <header className="nav">
        <a className="brand" href="/" aria-label="Molded Fortitude Consulting home">
          <span className="brand-mark">MFC</span>
          <span>{brand.name}</span>
        </a>
        <a className="nav-cta" href={offer.bookingLink} onClick={() => track('booking_link_click')}>Book the Call</a>
      </header>

      <section className="hero section">
        <div className="hero-copy">
          <p className="eyebrow">{offer.name}</p>
          <h1>Build Grant-Ready<br />Impact Systems<br />Your Team Can Trust</h1>
          <p className="hero-text">Molded Fortitude Consulting helps nonprofits, Indigenous communities, and service agencies replace reporting strain, scattered data, and compliance pressure with ethical, auditable workflows built for funding, clarity, and staff relief.</p>
          <div className="cta-row">
            <a className="button primary" href={offer.bookingLink} onClick={() => track('cta_click_book_call')}>Book the Systems Call <ArrowRight size={18} /></a>
            <a className="button secondary" href={offer.onePagerLink} onClick={() => track('cta_click_download_one_pager')}>View the 90-Day Impact One-Pager</a>
          </div>
          <p className="trust-line">Built for nonprofits, Indigenous groups, and mission-driven teams carrying too much administrative weight with too few resources.</p>
        </div>
        <div className="hero-panel" aria-label="Impact system preview">
          <div className="panel-header"><Workflow /> 90-Day Impact Build</div>
          <div className="metric"><span>Manual reporting strain</span><strong>Reduced</strong></div>
          <div className="metric"><span>Compliance visibility</span><strong>Clear</strong></div>
          <div className="metric"><span>Knowledge continuity</span><strong>Protected</strong></div>
          <div className="metric"><span>Decision support</span><strong>Live</strong></div>
        </div>
      </section>

      <section className="section problem">
        <p className="eyebrow">The Problem</p>
        <h2>The Mission Is Not the Problem. The Administrative Burden Is.</h2>
        <p>Your staff entered this work to serve people, protect culture, support families, build programs, and create change. Instead, too many hours disappear into disconnected spreadsheets, repeated data entry, funder reports, missing documents, and audit stress.</p>
        <div className="bullets">
          {['Staff burn out from preventable administrative load', 'Program data gets trapped across disconnected systems', 'Grant reporting becomes reactive instead of prepared', 'Leadership lacks real-time visibility', 'Institutional and cultural knowledge disappears when key staff leave', 'Funding opportunities are missed because evidence is scattered'].map((item) => <div className="bullet" key={item}><CheckCircle2 />{item}</div>)}
        </div>
        <p className="strong">MFC fixes the operating layer so your people can return their energy to the work that matters.</p>
      </section>

      <section className="section">
        <p className="eyebrow">The MFC Promise</p>
        <h2>Clarity. Compliance. Sustainability. Relief.</h2>
        <div className="card-grid four">
          {promises.map((item) => <article className="card" key={item.title}><h3>{item.title}</h3><p>{item.body}</p></article>)}
        </div>
      </section>

      <section className="section proof">
        <p className="eyebrow">Funder-Ready Proof</p>
        <h2>Built Around the Records Directors Actually Need</h2>
        <div className="card-grid four">
          {['Reporting calendars and deadline visibility', 'Document trails for audits and renewals', 'Clear ownership for data, files, and approvals', 'Staff handoff notes that reduce knowledge loss'].map((item) => (
            <article className="card" key={item}>
              <h3>{item}</h3>
              <p>Each part of the sprint is designed to make reporting easier to verify, explain, and maintain.</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section alt">
        <p className="eyebrow">Core Solutions</p>
        <h2>The Systems We Build</h2>
        <div className="card-grid">
          {solutions.map((item) => (
            <article className="card solution-card" key={item.title}>
              <h3>{item.title}</h3>
              <p><strong>Problem:</strong> {item.problem}</p>
              <p><strong>Solution:</strong> {item.solution}</p>
              <p><strong>Outcome:</strong> {item.outcome}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section ethics">
        <div>
          <p className="eyebrow">Ethical Technology Commitment</p>
          <h2>No Black-Box AI. No Data Games. No Cultural Drift.</h2>
          <p>Every MFC system is designed around transparency, accountability, and cultural safety. Technology should strengthen the mission, not control it.</p>
          <p className="strong">We build systems people can understand, trust, govern, and sustain.</p>
        </div>
        <div className="principles">
          {['Explainable workflows', 'Human approval points', 'Auditable automation', 'Clear data ownership', 'Minimal data collection', 'Secure role-based access', 'Respect for Indigenous data governance', 'OCAP principles where applicable and confirmed by the organization or community'].map((item) => <div className="bullet" key={item}><ShieldCheck />{item}</div>)}
        </div>
      </section>

      <section className="section">
        <p className="eyebrow">Implementation</p>
        <h2>The 90-Day Impact Build</h2>
        <div className="timeline">
          {phases.map((phase) => (
            <article className="phase" key={phase.title}>
              <span>{phase.label}</span>
              <h3>{phase.title}</h3>
              <p>{phase.weeks}</p>
              <ul>{phase.items.map((item) => <li key={item}>{item}</li>)}</ul>
            </article>
          ))}
        </div>
      </section>

      <section className="section outcomes alt">
        <p className="eyebrow">Outcomes</p>
        <h2>What Your Organization Walks Away With</h2>
        <div className="bullets two-col">
          {['A clear map of your data, workflows, reports, and compliance needs', 'A working lead/intake or program reporting form', 'A unified reporting dashboard', 'A grant compliance tracker', 'A document and knowledge hub structure', 'A repeatable reporting workflow', 'Staff training and handoff documentation', 'A 30-day action plan for continued improvement'].map((item) => <div className="bullet" key={item}><CheckCircle2 />{item}</div>)}
        </div>
      </section>

      <section className="section fit">
        <div>
          <p className="eyebrow">Fit</p>
          <h2>Built for Teams Carrying Heavy Missions</h2>
          <p>This is built for organizations that serve people, culture, wellness, housing, youth, employment, justice, health, education, and community development.</p>
        </div>
        <div className="fit-grid">
          <article className="card"><h3>Best fit</h3><ul><li>Nonprofits with scattered reporting systems</li><li>Indigenous organizations managing multiple funders</li><li>Service agencies preparing for audits</li><li>Program teams buried in admin</li><li>Organizations pursuing capacity-building funding</li></ul></article>
          <article className="card"><h3>Not fit</h3><ul><li>Teams looking for shiny AI with no governance</li><li>Organizations wanting automation without staff buy-in</li><li>Leaders who want speed without accountability</li></ul></article>
        </div>
      </section>

      <section className="section lead-section" id="one-pager">
        <div>
          <p className="eyebrow"><FileText size={16} /> Lead Magnet</p>
        <h2>Get the 90-Day Impact One-Pager</h2>
        <p>Use the one-page breakdown to see how MFC helps organizations reduce reporting strain, strengthen compliance, preserve knowledge, and build fundable systems in 90 days.</p>
        </div>
        <LeadForm />
      </section>

      <section className="section final-cta">
        <p className="eyebrow">Next Step</p>
        <h2>Book a Grant-Ready Systems Call</h2>
        <p>In one call, we identify where your team is losing time, where reporting risk lives, and which simple system improvements can create the fastest relief.</p>
        <a className="button primary" href={offer.bookingLink} onClick={() => track('booking_link_click')}>Book the Call <ArrowRight size={18} /></a>
        <p className="ship">Small, steady systems your staff can maintain.</p>
      </section>

      <footer className="footer">
        <p>{brand.name}</p>
        <p>{brand.email} · {brand.website}</p>
        <p>{brand.tagline}</p>
      </footer>
    </main>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(<React.StrictMode><App /></React.StrictMode>);
